// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {selx} from 'clsx';
import {jsx} from 'panel';

import {Translatable} from 'common/i18n';
import {ButtonEl} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {
  isArgumentListError,
  isBadRequestError,
  isCardinalityLimitError,
  isCohortDateRangeError,
  isDataClassificationError,
  isHPCListNotSupported,
  isInvalidFormulasError,
  isMissingDataGroupError,
  isMultipleHPCWithList,
  isTimeoutError,
  isUnauthorizedWorkspaceError,
  isUnsupportedAttributionError,
  isUserVisibleNotFoundError,
  isUserVisibleParamsError,
  isUserVisiblePreconditionFailedError,
  trackMixpanelTips,
  trimUserVisibleNotFoundError,
  trimUserVisibleParamsError,
  trimUserVisiblePreconditionFailedError,
} from 'common/report/queries/utils';
import {getMissingDatagroupMappedProperty, isMissingDatagroupMappedPropertyLookup} from 'common/report/util/properties';
import {SpinnerEl} from 'common/components/spinner';
import {SpinnerSize, SpinnerTheme} from 'common/components/spinner/types';
import {BaseQueryErrorType} from 'common/report/types';
import {WarningTheme} from 'common/components/warning/constants';
import {spans} from 'common/util/tracing';

import {QuerySamplingCtaEl} from './query-sampling-cta';
import {
  EMPTY_REPORT_HEADER,
  NoDataReason,
  ReportStatus,
  TIPS,
  WarningHeroReportName,
  WarningHeroTheme,
  WarningTrackingError,
} from './constants';
import {WarningHeroEl} from './index';
import styles from './presets.module.styl';
import {NoDataHeroPanel} from './no-data-hero';

export class TipFactory {
  static index = -1;
  static startEpoch = 0;

  static getTip() {
    if (TipFactory.index === -1) {
      TipFactory.index = Math.floor(Math.random() * TIPS.length);
    }

    return TIPS[TipFactory.index];
  }

  static resetTip() {
    TipFactory.index = -1;
    TipFactory.startEpoch = 0;
  }
}

export function TipsHero() {
  const tip = TipFactory.getTip();

  return (
    <WarningHeroEl
      attrs={{
        theme: WarningTheme.Tip,
      }}
      params={{
        headerText: tip.title || ``,
        bodyText: tip.body || ``,
      }}
      hook={{
        insert: () => {
          trackMixpanelTips(TipFactory.index);
          TipFactory.startEpoch = Date.now();
        },
        destroy: () => {
          trackMixpanelTips(TipFactory.index, Date.now() - TipFactory.startEpoch);
          TipFactory.resetTip();
        },
      }}
    />
  );
}

export function LoadingHero(params: {bodyText?: string; headerText?: string} = {}) {
  const {headerText, bodyText} = params || {};
  return (
    <WarningHeroEl
      attrs={{
        elref: `loadingHero`,
        theme: WarningHeroTheme.Loading,
      }}
      params={{
        headerText: headerText || ``,
        bodyText: bodyText || ``,
      }}
    />
  );
}

export function SensitiveDataHero({bodyText, reportName}: {bodyText?: string; reportName: WarningHeroReportName}) {
  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.Sensitive,
        report: reportName,
      }}
      params={{
        headerText: ({t}) => t(`warning-hero.sensitive-data`),
        bodyText: bodyText || ``,
      }}
    />
  );
}

export function UnsupportedActionHero({
  bodyText,
  reportName,
}: {
  bodyText: Translatable;
  reportName: WarningHeroReportName;
}) {
  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.Error,
        report: reportName,
        'tracking-error': WarningTrackingError.UnsupportedActionError,
      }}
      params={{
        headerText: ({t}) => t(`warning-hero.unsupported`),
        bodyText,
      }}
    />
  );
}

export function UnauthorizedWorkspaceError({reportName}: {reportName: WarningHeroReportName}) {
  // Most commonly raised for dashboard digests, as the page normally redirects to a valid workspace
  // (see `findCurrentValidWorkspaceId` in chrome-header)
  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.Sensitive,
        report: reportName,
      }}
      params={{
        headerText: ({t}) => t(`warning-hero.unauthorized-workspace`) || ``,
        bodyText: ``,
      }}
    />
  );
}

export function MissingDataGroupMappingHero({
  property,
  reportName,
  isLookupProperty,
}: {
  property: string;
  reportName: WarningHeroReportName;
  isLookupProperty: boolean;
}) {
  if (isLookupProperty) {
    return (
      <WarningHeroEl
        attrs={{
          theme: WarningHeroTheme.Error,
          report: reportName,
          'tracking-error': WarningTrackingError.MissingMappedDatagroup,
        }}
        params={{
          headerText: ({t}) => t(`warning-hero.unsupported`),
          bodyText: ({t}) => t(`warning-hero.lookup-property-unavailable`, {property}),
        }}
      />
    );
  }

  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.Error,
        report: reportName,
        'tracking-error': WarningTrackingError.MissingMappedDatagroup,
      }}
      params={{
        headerText: ({t}) => t(`warning-hero.unsupported`),
        bodyText: ({t}) => t(`warning-hero.property-unavailable`, {property}),
      }}
    />
  );
}

export function ServerErrorHero({
  headerText,
  bodyText,
  reportName,
}: {
  headerText?: Translatable;
  bodyText?: Translatable;
  reportName: WarningHeroReportName;
}) {
  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.Error,
        report: reportName,
      }}
      params={{
        headerText: headerText || (({t}) => t(`warning-hero.server-error`)),
        bodyText: bodyText || ``,
      }}
    />
  );
}

export function EmptyDashboardAddReportHeroTextCards(_props = null, childrenActions) {
  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.EmptyReport,
        report: WarningHeroReportName.Dashboards,
      }}
      params={{
        headerText: `Add a new chart, text or use a template to get started.`,
      }}
    >
      {childrenActions}
    </WarningHeroEl>
  );
}

export function EmptyLookupTableHero({onImportLookupTableClick}) {
  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.EmptyReport,
        report: WarningHeroReportName.LookupTables,
      }}
      params={{
        headerText: EMPTY_REPORT_HEADER[WarningHeroReportName.LookupTables],
      }}
    >
      <ButtonEl
        attrs={{
          icon: `plus`,
          theme: ButtonTheme.Primary,
        }}
        on={{click: onImportLookupTableClick}}
      >
        Import Lookup Table
      </ButtonEl>
    </WarningHeroEl>
  );
}

export function InitialReportHero({reportName}: {reportName: WarningHeroReportName}) {
  const theme = WarningHeroTheme.EmptyReport;
  return (
    <WarningHeroEl
      attrs={{
        theme,
        report: reportName,
      }}
      params={{
        headerText: EMPTY_REPORT_HEADER[reportName],
      }}
    />
  );
}
export function EmptyReportHero({reportName}: {reportName: WarningHeroReportName}) {
  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.EmptyReport,
        report: reportName,
      }}
      params={{
        headerText: EMPTY_REPORT_HEADER[reportName],
      }}
    />
  );
}

export function InvalidFormulasHero({reportName}: {reportName: WarningHeroReportName}) {
  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.Error,
        report: reportName,
      }}
      params={{
        headerText: ({t}) => t(`warning-hero.invalid-formulas`),
      }}
    />
  );
}

const CONTACT_SUPPORT_MESSAGE = ({t}) => t(`warning-hero.contact-support`);
export function ReportStatusHero({
  reportName,
  queryError,
  reportStatus,
  showQuerySamplingCTA,
  showMpTips,
  enableQuerySampling,
  noDataReason,
}: {
  reportName: WarningHeroReportName;
  queryError?: BaseQueryErrorType;
  reportStatus?: ReportStatus;
  showQuerySamplingCTA?: boolean;
  showMpTips?: boolean;
  enableQuerySampling?: (ev) => void;
  noDataReason?: NoDataReason;
}) {
  if (queryError) {
    const {error, status, data} = queryError;
    if (isDataClassificationError(error) || status === 423) {
      return <SensitiveDataHero bodyText={error} reportName={reportName} />;
    } else if (isUnauthorizedWorkspaceError({status, error})) {
      return <UnauthorizedWorkspaceError reportName={reportName} />;
    } else if (isMissingDataGroupError(error)) {
      const property = getMissingDatagroupMappedProperty(data);
      const isLookupProperty = isMissingDatagroupMappedPropertyLookup(data);
      return (
        <MissingDataGroupMappingHero property={property} reportName={reportName} isLookupProperty={isLookupProperty} />
      );
    } else if (isCardinalityLimitError(error)) {
      return <UnsupportedActionHero bodyText={({t}) => t(`warning-hero.cardinality-limit`)} reportName={reportName} />;
    } else if (isArgumentListError(error)) {
      return <UnsupportedActionHero bodyText={({t}) => t(`warning-hero.too-many-arguments`)} reportName={reportName} />;
    } else if (isInvalidFormulasError(error)) {
      return <InvalidFormulasHero reportName={reportName} />;
    } else if (isUnsupportedAttributionError(error)) {
      return <UnsupportedActionHero bodyText={error} reportName={reportName} />;
    } else if (isCohortDateRangeError(error)) {
      return <UnsupportedActionHero bodyText={error} reportName={reportName} />;
    } else if (isHPCListNotSupported(error)) {
      return (
        <UnsupportedActionHero bodyText={({t}) => t(`warning-hero.list-property-constant`)} reportName={reportName} />
      );
    } else if (isMultipleHPCWithList(error)) {
      return (
        <UnsupportedActionHero bodyText={({t}) => t(`warning-hero.hpc-limit-with-list`)} reportName={reportName} />
      );
    } else if (isUserVisibleParamsError(error)) {
      return <UnsupportedActionHero bodyText={trimUserVisibleParamsError(error)} reportName={reportName} />;
    } else if (isUserVisiblePreconditionFailedError(error)) {
      return <UnsupportedActionHero bodyText={trimUserVisiblePreconditionFailedError(error)} reportName={reportName} />;
    } else if (isUserVisibleNotFoundError(error)) {
      return <UnsupportedActionHero bodyText={trimUserVisibleNotFoundError(error)} reportName={reportName} />;
    } else if (status === 412) {
      return (
        <UnsupportedActionHero bodyText={({t}) => t(`warning-hero.precondition-failed`)} reportName={reportName} />
      );
    } else if (isBadRequestError(status) || status === 422) {
      return (
        <ServerErrorHero
          headerText={({t}) => t(`warning-hero.bad-request`)}
          bodyText={CONTACT_SUPPORT_MESSAGE}
          reportName={reportName}
        />
      );
    } else if (isTimeoutError(status)) {
      return (
        <ServerErrorHero
          headerText={({t}) => t(`warning-hero.timeout`)}
          bodyText={({t, print}) => t(`warning-hero.timeout-remedy`) + `\n` + print(CONTACT_SUPPORT_MESSAGE)}
          reportName={reportName}
        />
      );
    }
    return <ServerErrorHero bodyText={CONTACT_SUPPORT_MESSAGE} reportName={reportName} />;
  }

  switch (reportStatus) {
    case ReportStatus.Error:
      // Fallback case for generic report errors that might be raised by the UX pre-query, such as
      // invalid bookmarks.
      return <ServerErrorHero bodyText={CONTACT_SUPPORT_MESSAGE} reportName={reportName} />;
    case ReportStatus.Initial:
      return <InitialReportHero reportName={reportName} />;
    case ReportStatus.Empty:
      return <EmptyReportHero reportName={reportName} />;
    case ReportStatus.NoData:
      return (
        <NoDataHeroPanel
          bodyText={({t}) =>
            t(
              noDataReason === NoDataReason.EmptyProject
                ? `insights.no-data.remedy-empty-project`
                : `insights.no-data.remedy`,
            )
          }
          reportName={reportName}
          reason={noDataReason}
        />
      );
    case ReportStatus.Loading:
      return (
        <div
          hook={{
            insert: () => spans.getOrCreateSpan({id: `report.loading`, parentId: `report-query`}),
            destroy: () => spans.endSpanById(`report.loading`),
          }}
          sel={selx(styles.loadingState)}
        >
          {/* IMPORTANT: do not change .chart-loader selector unless modifying screenshot_service/report_screenshot.js first */}
          <SpinnerEl
            sel='.chart-loader'
            attrs={{size: SpinnerSize.Medium, elref: `chartLoader`, theme: SpinnerTheme.Fancy}}
          />
          <div sel='tip-container' style={{height: `120px`}}>
            {showQuerySamplingCTA ? (
              <QuerySamplingCtaEl attrs={{slot: `loading`}} on={{enable: enableQuerySampling}} />
            ) : showMpTips ? (
              <TipsHero />
            ) : null}
          </div>
        </div>
      );
    case ReportStatus.Success:
      // Success should not show any warning hero, return null to render the report normally
      return null;
    default:
      return null;
  }
}
